// Trades, dividends, the live portfolio and its performance.

import type {
  CreateDividendInput,
  CreateTradeInput,
  Dividend,
  DividendListResponse,
  DividendSortKey,
  FxAttributionResponse,
  FxPerformanceResponse,
  MarketNewsResponse,
  PortfolioPerformance,
  PortfolioResponse,
  PositionEpisode,
  Trade,
  TradeListResponse,
  TradeSortKey,
  UpdateTradeInput,
  UpdateDividendInput,
} from "@/finance/model/wire.js";
import { api, kept, polled, qk } from "@/platform/client";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MARKET_REFRESH_MS } from "@/finance/ui/queries/market.js";

// ---- investments: trades / dividends / portfolio / performance ----
export interface TradeFilters {
  search?: string;
  accountIds?: number[];
  type?: "buy" | "sell";
  from?: string;
  to?: string;
  sort?: TradeSortKey;
  dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export function useTrades(f: TradeFilters) {
  const sort = f.sort ?? "date";
  const dir = f.dir ?? "desc";
  const limit = f.limit ?? 100;
  const offset = f.offset ?? 0;
  const p = new URLSearchParams();
  if (f.search) p.set("search", f.search);
  if (f.accountIds?.length) p.set("accountIds", f.accountIds.join(","));
  if (f.type) p.set("type", f.type);
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  p.set("sort", sort);
  p.set("dir", dir);
  p.set("limit", String(limit));
  p.set("offset", String(offset));
  return useQuery({
    queryKey: qk.trades.list([
      f.search ?? "",
      (f.accountIds ?? []).join(","),
      f.type ?? "",
      f.from ?? "",
      f.to ?? "",
      sort,
      dir,
      offset,
      limit,
    ]),
    queryFn: () => api.get<TradeListResponse>(`/trades?${p.toString()}`),
    placeholderData: (prev) => prev,
  });
}

export interface DividendFilters {
  search?: string;
  accountIds?: number[];
  from?: string;
  to?: string;
  sort?: DividendSortKey;
  dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export function useDividends(f: DividendFilters) {
  const sort = f.sort ?? "date";
  const dir = f.dir ?? "desc";
  const limit = f.limit ?? 100;
  const offset = f.offset ?? 0;
  const p = new URLSearchParams();
  if (f.search) p.set("search", f.search);
  if (f.accountIds?.length) p.set("accountIds", f.accountIds.join(","));
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  p.set("sort", sort);
  p.set("dir", dir);
  p.set("limit", String(limit));
  p.set("offset", String(offset));
  return useQuery({
    queryKey: qk.dividends.list([
      f.search ?? "",
      (f.accountIds ?? []).join(","),
      f.from ?? "",
      f.to ?? "",
      sort,
      dir,
      offset,
      limit,
    ]),
    queryFn: () => api.get<DividendListResponse>(`/dividends?${p.toString()}`),
    placeholderData: (prev) => prev,
  });
}

export function usePortfolio() {
  return useQuery({
    queryKey: qk.portfolio.all,
    queryFn: () => api.get<PortfolioResponse>("/portfolio"),
    ...polled(MARKET_REFRESH_MS),
  });
}

/** Every period a position was held, closed ones included. Derived from the
 *  lots on each read, and unaffected by a live quote except for the mark on the
 *  open ones — so it does not need the market's refresh interval. */
export function usePortfolioEpisodes() {
  return useQuery({
    queryKey: qk.portfolio.episodes,
    queryFn: () => api.get<PositionEpisode[]>("/portfolio/episodes"),
  });
}

export const portfolioPerformanceQuery = queryOptions({
  queryKey: qk.portfolioPerformance.all,
  queryFn: () => api.get<PortfolioPerformance>("/performance"),
  // heavier (fans out to a year of daily history) → refresh less aggressively
  ...polled(5 * MARKET_REFRESH_MS),
  ...kept,
});

export const usePortfolioPerformance = () => useQuery(portfolioPerformanceQuery);

export function useFxAttribution() {
  return useQuery({
    queryKey: qk.fxAttribution.all,
    queryFn: () => api.get<FxAttributionResponse>("/analytics/fx-attribution"),
    ...polled(5 * MARKET_REFRESH_MS),
  });
}

export function useFxPerformance() {
  return useQuery({
    queryKey: qk.fxPerformance.all,
    queryFn: () => api.get<FxPerformanceResponse>("/analytics/fx-performance"),
    // heavy (fans out to 2y of daily history per symbol) → refresh gently
    ...polled(10 * MARKET_REFRESH_MS),
  });
}

export const marketNewsQuery = queryOptions({
  queryKey: qk.marketNews.all,
  queryFn: () => api.get<MarketNewsResponse>("/market/news"),
  ...polled(10 * MARKET_REFRESH_MS),
  ...kept,
});

export const useMarketNews = () => useQuery(marketNewsQuery);

function invalidateTrades(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: qk.trades.all });
  qc.invalidateQueries({ queryKey: qk.dividends.all });
  // A trade opens or closes a lot, moves a position, and the balance the
  // application maintains from them.
  qc.invalidateQueries({ queryKey: qk.lots.all });
  qc.invalidateQueries({ queryKey: qk.positions.all });
  qc.invalidateQueries({ queryKey: qk.balances.all });
  qc.invalidateQueries({ queryKey: qk.portfolio.all });
  qc.invalidateQueries({ queryKey: qk.portfolioPerformance.all });
  qc.invalidateQueries({ queryKey: qk.fxAttribution.all });
  qc.invalidateQueries({ queryKey: qk.fxPerformance.all });
  qc.invalidateQueries({ queryKey: qk.marketNews.all });
  qc.invalidateQueries({ queryKey: qk.cashflow.all });
}

export function useCreateTrade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTradeInput) => api.post<Trade>("/trades", input),
    onSuccess: () => {
      invalidateTrades(qc);
      qc.invalidateQueries({ queryKey: qk.accounts.all });
    },
  });
}

export function useCreateDividend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDividendInput) => api.post<Dividend>("/dividends", input),
    onSuccess: () => {
      invalidateTrades(qc);
      qc.invalidateQueries({ queryKey: qk.accounts.all });
    },
  });
}

export function useUpdateTrade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: UpdateTradeInput }) =>
      api.patch<Trade>(`/trades/${id}`, patch),
    onSuccess: () => invalidateTrades(qc),
  });
}

/** Dividends have their own endpoint, because they have their own table — and
 *  therefore their own ids. One endpoint taking either could only guess which
 *  kind an id meant, and would eventually guess wrong. */
export function useUpdateDividend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: UpdateDividendInput }) =>
      api.patch<Dividend>(`/dividends/${id}`, patch),
    onSuccess: () => invalidateTrades(qc),
  });
}

export function useDeleteTrades() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.post<{ deleted: number }>("/trades/delete", { ids }),
    onSuccess: () => invalidateTrades(qc),
  });
}
